import {
  MAX_PLAN_KEY,
  MONTHLY_BILLING_CYCLE,
  PRO_PLAN_KEY,
  YEARLY_BILLING_CYCLE,
  discountedAmount,
  offerTargetsPlan,
  resolvePromoOffer,
} from './plan_selection';
import { safeReturnToPath } from './auth_navigation';

// An offer link's chosen plan and promo code travel through sign-in, signup,
// email verification and password reset as a `return_to` path, e.g.
// /plan-selection?plan=monthly&offer=pro50x12&resume=checkout. This module
// reads that path back so every auth screen can show what's waiting and the
// plan page can continue to checkout.

export const CHECKOUT_RESUME_PARAM = 'resume';
export const CHECKOUT_RESUME_VALUE = 'checkout';

const CHECKOUT_PLANS = {
  monthly: { planKey: PRO_PLAN_KEY, billingCycle: MONTHLY_BILLING_CYCLE },
  yearly: { planKey: PRO_PLAN_KEY, billingCycle: YEARLY_BILLING_CYCLE },
  max_monthly: { planKey: MAX_PLAN_KEY, billingCycle: MONTHLY_BILLING_CYCLE },
  max_yearly: { planKey: MAX_PLAN_KEY, billingCycle: YEARLY_BILLING_CYCLE },
};

const OFFER_PATHS = new Set(['/plan-selection', '/start-checkout']);

export const checkoutPlanFor = (planKey, billingCycle) => {
  const yearly = billingCycle === YEARLY_BILLING_CYCLE;
  if (planKey === MAX_PLAN_KEY) return yearly ? 'max_yearly' : 'max_monthly';
  if (planKey === PRO_PLAN_KEY) return yearly ? 'yearly' : 'monthly';
  return null;
};

export const checkoutIntentFromPath = (returnTo) => {
  const path = safeReturnToPath(returnTo);
  if (!path) return null;

  let url;
  try {
    url = new URL(path, 'https://app.invalid');
  } catch (_error) {
    return null;
  }
  if (!OFFER_PATHS.has(url.pathname)) return null;

  const requested = CHECKOUT_PLANS[url.searchParams.get('plan')] || null;
  let offer = resolvePromoOffer(url.searchParams.get('offer'));
  let planKey = null;
  let billingCycle = null;

  if (requested) {
    planKey = requested.planKey;
    billingCycle = requested.billingCycle;
    // Checkout ignores an offer for a plan it doesn't discount; so do we.
    if (offer && !(offerTargetsPlan(offer, planKey) && offer.billingCycle === billingCycle)) {
      offer = null;
    }
  } else if (offer) {
    planKey = offer.targetPlans.length === 1 ? offer.targetPlans[0] : null;
    billingCycle = offer.billingCycle;
  } else {
    return null;
  }

  return {
    offer,
    planKey,
    billingCycle,
    checkoutPlan: planKey ? checkoutPlanFor(planKey, billingCycle) : null,
    resumeCheckout:
      url.pathname === '/start-checkout' ||
      url.searchParams.get(CHECKOUT_RESUME_PARAM) === CHECKOUT_RESUME_VALUE,
  };
};

export const planPricingFromGlobals = () => {
  const pricing =
    (typeof window !== 'undefined' && window.globals && window.globals.pricing) || {};

  return {
    proMonthly: pricing.monthly_amount ?? 19.99,
    proYearly: pricing.yearly_amount ?? 199.99,
    maxMonthly: pricing.max_monthly_amount ?? 149.99,
    maxYearly: pricing.max_yearly_amount ?? 1499.99,
  };
};

const priceFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export const formatUsd = (amount) =>
  amount == null || Number.isNaN(Number(amount)) ? null : priceFormatter.format(Number(amount));

const regularAmountFor = (pricing, planKey, billingCycle) => {
  const yearly = billingCycle === YEARLY_BILLING_CYCLE;
  if (planKey === MAX_PLAN_KEY) return yearly ? pricing.maxYearly : pricing.maxMonthly;
  return yearly ? pricing.proYearly : pricing.proMonthly;
};

// Copy for the summary card shown on every auth screen.
export const describeCheckoutIntent = (intent, pricing = planPricingFromGlobals()) => {
  if (!intent) return null;

  const { offer, planKey, billingCycle } = intent;
  const yearly = billingCycle === YEARLY_BILLING_CYCLE;
  const planName =
    planKey === MAX_PLAN_KEY ? 'Incite MAX' : planKey === PRO_PLAN_KEY ? 'Incite Pro' : 'Incite Pro or MAX';
  const cycleLabel = yearly ? 'Annual' : 'Monthly';
  const regular = regularAmountFor(pricing, planKey, billingCycle);
  const regularPer = yearly ? '/yr' : '/mo';

  if (!offer) {
    return {
      badge: null,
      code: null,
      planName,
      cycleLabel,
      price: formatUsd(yearly ? regular / 12 : regular),
      per: '/mo',
      strike: null,
      detail: yearly ? `Billed annually at ${formatUsd(regular)}.` : 'Billed monthly.',
    };
  }

  const discounted = discountedAmount(regular, offer.discountPercent);
  const percentLabel = `${offer.discountPercent}% off`;

  return {
    badge: `${offer.discountPercent}% OFF`,
    code: offer.displayCode || offer.key.toUpperCase(),
    planName,
    cycleLabel,
    price: formatUsd(discounted),
    per: yearly ? ' first year' : '/mo',
    strike: formatUsd(regular),
    detail: planKey
      ? `${percentLabel} ${offer.durationLabel}, then ${formatUsd(regular)}${regularPer}.`
      : `${percentLabel} ${offer.durationLabel} on Pro or MAX annual. Pro shown; MAX is ${formatUsd(
        discountedAmount(regularAmountFor(pricing, MAX_PLAN_KEY, billingCycle), offer.discountPercent),
      )}.`,
  };
};

export const startCheckoutPath = (intent) => {
  if (!intent?.checkoutPlan) return null;

  const params = new URLSearchParams({ plan: intent.checkoutPlan });
  if (intent.offer) params.set('offer', intent.offer.key);
  return `/start-checkout?${params.toString()}`;
};

// What the plan page does when it is reached with resume=checkout after
// sign-in, signup, verification or a password reset.
export const planSelectionResumeAction = ({
  resumeRequested = false,
  userResolved = false,
  hasUser = false,
  emailConfirmed = null,
  isPaidSubscriber = false,
  maxTrialMode = false,
  checkoutPlan = null,
} = {}) => {
  if (!resumeRequested) return 'none';
  if (!userResolved) return 'wait';
  if (!hasUser) return 'none';
  if (emailConfirmed === false) return 'verify-email';
  // Subscribers change plans from the subscription flow, never a second checkout.
  if (isPaidSubscriber || maxTrialMode || !checkoutPlan) return 'none';
  return 'checkout';
};

// How an offer relates to an existing paid subscription. Stripe subscribers
// can use an offer only through a plan change (another billing period or an
// upgrade to MAX), which the plan page previews before anything is charged.
export const subscriberOfferState = ({
  offer = null,
  isPaidSubscriber = false,
  isStripeSubscriber = false,
  isTrialingSubscriber = false,
  subscriptionProvider = null,
  currentTier = null,
  currentBillingCycle = null,
  maxTrialMode = false,
} = {}) => {
  if (!offer || !isPaidSubscriber || maxTrialMode) return null;
  if (!isStripeSubscriber) return { kind: 'external', provider: subscriptionProvider };
  if (isTrialingSubscriber) return { kind: 'trial' };

  const tier = currentTier === MAX_PLAN_KEY ? MAX_PLAN_KEY : PRO_PLAN_KEY;
  if (
    offerTargetsPlan(offer, tier) &&
    currentBillingCycle &&
    currentBillingCycle !== offer.billingCycle
  ) {
    return { kind: 'switch', targetPlan: checkoutPlanFor(tier, offer.billingCycle) };
  }
  if (tier === PRO_PLAN_KEY && offerTargetsPlan(offer, MAX_PLAN_KEY)) {
    return { kind: 'upgrade', targetPlan: checkoutPlanFor(MAX_PLAN_KEY, offer.billingCycle) };
  }
  return { kind: 'not-applicable' };
};

// Two tabs of the same browser can both reach the end of the flow (the tab
// that signed up and the tab the email link opened). The first one to start
// checkout leaves a marker so the other says so instead of opening a second
// checkout. Different browsers never share it, which is intended.
const CHECKOUT_HANDOFF_KEY = 'incite.checkoutHandoff';
const CHECKOUT_HANDOFF_TTL_MS = 10 * 60 * 1000;

const browserStorage = () => {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch (_error) {
    return null;
  }
};

export const markCheckoutHandoff = (checkoutPath, { storage = browserStorage(), now = Date.now() } = {}) => {
  if (!checkoutPath) return;
  try {
    storage?.setItem(CHECKOUT_HANDOFF_KEY, JSON.stringify({ checkoutPath, at: now }));
  } catch (_error) {
    // Private mode or full storage: the worst case is a second checkout tab.
  }
};

export const recentCheckoutHandoff = (returnTo, { storage = browserStorage(), now = Date.now() } = {}) => {
  const checkoutPath = startCheckoutPath(checkoutIntentFromPath(returnTo));
  if (!checkoutPath) return false;

  try {
    const marker = JSON.parse(storage?.getItem(CHECKOUT_HANDOFF_KEY) || 'null');
    return !!marker && marker.checkoutPath === checkoutPath && now - Number(marker.at) < CHECKOUT_HANDOFF_TTL_MS;
  } catch (_error) {
    return false;
  }
};
